import React, { useState, useEffect, useReducer, useContext } from "react";
import toastUtils from "../../utils/toastUtils";

import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import Fab from "@material-ui/core/Fab";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import Box from "@material-ui/core/Box";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import StopIcon from "@material-ui/icons/Stop";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import GetAppIcon from "@material-ui/icons/GetApp";
import AndroidIcon from "@material-ui/icons/Android";
import PeopleIcon from "@material-ui/icons/People";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import EventIcon from "@material-ui/icons/Event";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import ForbiddenPage from "../../components/ForbiddenPage";
import usePlanPermissions from "../../hooks/usePlanPermissions";
import { AuthContext } from "../../context/Auth/AuthContext";

import SDRCampaignModal from "../../components/SDRCampaignModal";
import SDRLeadUploadModal from "../../components/SDRLeadUploadModal";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    backgroundColor: theme.palette.background.paper,
  },
  statsCard: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
  },
  statsContent: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  statsIcon: {
    fontSize: 40,
    marginRight: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  statsText: {
    flex: 1,
  },
  statsNumber: {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  tabPanel: {
    paddingTop: theme.spacing(2),
  },
  fab: {
    position: "fixed",
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 1000,
  },
  actionButton: {
    marginRight: theme.spacing(1),
  },
  statusChip: {
    fontSize: "0.75rem",
    height: 24,
  },
  campaignCard: {
    margin: theme.spacing(1),
    cursor: "pointer",
    transition: "transform 0.2s",
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[4],
    },
  },
  campaignTitle: {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  sectionTitle: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  searchContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
  },
  tabsContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_CAMPAIGNS") {
    return { ...state, campaigns: action.payload, loading: false };
  }
  if (action.type === "UPDATE_CAMPAIGN") {
    const campaigns = state.campaigns.map((campaign) =>
      campaign.id === action.payload.id ? action.payload : campaign
    );
    return { ...state, campaigns };
  }
  if (action.type === "DELETE_CAMPAIGN") {
    const campaigns = state.campaigns.filter(
      (campaign) => campaign.id !== action.payload
    );
    return { ...state, campaigns };
  }
  if (action.type === "RESET") {
    return { ...state, loading: true };
  }
};

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`sdr-tabpanel-${index}`}
      aria-labelledby={`sdr-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          {children}
        </Box>
      )}
    </div>
  );
}

const SDRDashboard = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { permissions } = usePlanPermissions();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [campaigns, dispatch] = useReducer(reducer, {
    campaigns: [],
    loading: true,
  });
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState({
    totalCampaigns: 0,
    totalLeads: 0,
    qualifiedLeads: 0,
    meetingsScheduled: 0,
  });


  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchCampaigns();
      fetchStats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  const fetchCampaigns = async () => {
    try {
      const { data } = await api.get("/sdr/campaigns", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CAMPAIGNS", payload: data.data });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await api.get("/sdr/campaigns");
      const campaigns = data.data;

      const stats = {
        totalCampaigns: campaigns.length,
        totalLeads: campaigns.reduce((sum, c) => sum + (c.totalProspects || 0), 0),
        qualifiedLeads: campaigns.reduce((sum, c) => sum + (c.qualifiedLeads || 0), 0),
        meetingsScheduled: campaigns.reduce((sum, c) => sum + (c.meetingsScheduled || 0), 0),
      };

      setStats(stats);
    } catch (err) {
      console.error("Erro ao buscar estatísticas:", err);
    }
  };

  const handleOpenCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(true);
  };

  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setCampaignModalOpen(true);
  };

  const handleCloseCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(false);
  };

  const handleSaveCampaign = (campaign) => {
    if (selectedCampaign) {
      dispatch({ type: "UPDATE_CAMPAIGN", payload: campaign });
    } else {
      dispatch({ type: "LOAD_CAMPAIGNS", payload: [...campaigns.campaigns, campaign] });
    }
    fetchStats();
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/sdr/campaigns/${campaignId}`);
      dispatch({ type: "DELETE_CAMPAIGN", payload: +campaignId });
      toastUtils.success(i18n.t("sdr.toasts.campaignDeleted"));
      fetchStats();
    } catch (err) {
      toastError(err);
    }
    setDeletingCampaign(null);
    setConfirmModalOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const downloadTemplate = async () => {
    try {
      const response = await api.get("/sdr/download-template", {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "template_leads.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toastUtils.success(i18n.t("sdr.toasts.templateDownloaded"));
    } catch (err) {
      toastError(err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "primary";
      case "paused":
        return "default";
      case "completed":
        return "secondary";
      case "draft":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    return i18n.t(`sdr.campaignStatus.${status}`);
  };

  // Temporariamente comentado para desenvolvimento
  // if (!planCompany?.useIASDR) {
  //   return <ForbiddenPage />;
  // }

  const StatsCards = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <Card className={classes.statsCard}>
          <CardContent className={classes.statsContent}>
            <AndroidIcon className={classes.statsIcon} />
            <div className={classes.statsText}>
              <Typography variant="h4" component="div" className={classes.statsNumber}>
                {stats.totalCampaigns}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {i18n.t("sdr.stats.totalCampaigns")}
              </Typography>
            </div>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card className={classes.statsCard}>
          <CardContent className={classes.statsContent}>
            <PeopleIcon className={classes.statsIcon} />
            <div className={classes.statsText}>
              <Typography variant="h4" component="div" className={classes.statsNumber}>
                {stats.totalLeads}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {i18n.t("sdr.stats.totalLeads")}
              </Typography>
            </div>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card className={classes.statsCard}>
          <CardContent className={classes.statsContent}>
            <TrendingUpIcon className={classes.statsIcon} />
            <div className={classes.statsText}>
              <Typography variant="h4" component="div" className={classes.statsNumber}>
                {stats.qualifiedLeads}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {i18n.t("sdr.stats.qualifiedLeads")}
              </Typography>
            </div>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card className={classes.statsCard}>
          <CardContent className={classes.statsContent}>
            <EventIcon className={classes.statsIcon} />
            <div className={classes.statsText}>
              <Typography variant="h4" component="div" className={classes.statsNumber}>
                {stats.meetingsScheduled}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {i18n.t("sdr.stats.meetingsScheduled")}
              </Typography>
            </div>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const CampaignsList = () => (
    <>
      <div className={classes.searchContainer}>
        <TextField
          fullWidth
          placeholder={i18n.t("sdr.searchPlaceholder")}
          type="search"
          value={searchParam}
          onChange={(e) => setSearchParam(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ color: "grey" }} />
              </InputAdornment>
            ),
          }}
        />
        <Tooltip title={i18n.t("sdr.downloadTemplate")}>
          <Button
            variant="outlined"
            onClick={downloadTemplate}
            startIcon={<GetAppIcon />}
            color="primary"
          >
            {i18n.t("sdr.downloadTemplate")}
          </Button>
        </Tooltip>
        <Tooltip title={i18n.t("sdr.uploadLeads")}>
          <Button
            variant="outlined"
            onClick={() => setUploadModalOpen(true)}
            startIcon={<CloudUploadIcon />}
            color="primary"
          >
            {i18n.t("sdr.uploadLeads")}
          </Button>
        </Tooltip>
      </div>

      <Grid container spacing={2}>
        {loading && (
          <Grid item xs={12}>
            <Paper className={classes.mainPaper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: "1%" }}></TableCell>
                    <TableCell>{i18n.t("sdr.table.name")}</TableCell>
                    <TableCell>{i18n.t("sdr.table.status")}</TableCell>
                    <TableCell>{i18n.t("sdr.table.leads")}</TableCell>
                    <TableCell>{i18n.t("sdr.table.qualified")}</TableCell>
                    <TableCell>{i18n.t("sdr.table.meetings")}</TableCell>
                    <TableCell>{i18n.t("sdr.table.actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from(new Array(5)).map((_, index) => (
                    <TableRowSkeleton key={index} columns={7} />
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        )}

        {!loading && campaigns.campaigns.map((campaign) => (
          <Grid item xs={12} sm={6} md={4} key={campaign.id}>
            <Card className={classes.campaignCard}>
              <CardContent>
                <Typography variant="h6" component="div" gutterBottom className={classes.campaignTitle}>
                  {campaign.name}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {campaign.description}
                </Typography>
                <div style={{ marginTop: 16, marginBottom: 16 }}>
                  <Chip
                    label={getStatusLabel(campaign.status)}
                    color={getStatusColor(campaign.status)}
                    size="small"
                    className={classes.statusChip}
                  />
                </div>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>{campaign.totalProspects || 0}</strong> {i18n.t("sdr.leads")}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>{campaign.qualifiedLeads || 0}</strong> {i18n.t("sdr.qualified")}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>{campaign.meetingsScheduled || 0}</strong> {i18n.t("sdr.meetings")}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>{((campaign.qualifiedLeads || 0) / Math.max(campaign.totalProspects || 1, 1) * 100).toFixed(1)}%</strong> {i18n.t("sdr.conversion")}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
              <CardActions>
                <Tooltip title={i18n.t("common.edit")}>
                  <IconButton
                    size="small"
                    onClick={() => handleEditCampaign(campaign)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={i18n.t("common.delete")}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setDeletingCampaign(campaign);
                      setConfirmModalOpen(true);
                    }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={i18n.t("sdr.viewLeads")}>
                  <IconButton
                    size="small"
                    onClick={() => history.push(`/sdr/campaigns/${campaign.id}/leads`)}
                  >
                    <PeopleIcon />
                  </IconButton>
                </Tooltip>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </>
  );

  return (
    <MainContainer>
      <MainHeader>
        <Title>
          <AndroidIcon style={{ marginRight: 8 }} />
          {i18n.t("sdr.title")}
        </Title>
      </MainHeader>

      <Paper className={classes.mainPaper}>
        <StatsCards />

        <div className={classes.tabsContainer}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
          >
          <Tab label={i18n.t("sdr.tabs.campaigns")} />
          <Tab label={i18n.t("sdr.tabs.analytics")} />
          </Tabs>
        </div>

        <TabPanel value={tabValue} index={0}>
          <CampaignsList />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" className={classes.sectionTitle}>
            {i18n.t("sdr.analytics.coming")}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {i18n.t("sdr.analytics.description")}
          </Typography>
        </TabPanel>
      </Paper>

      <Fab
        color="primary"
        aria-label="add"
        className={classes.fab}
        onClick={handleOpenCampaignModal}
      >
        <AddIcon />
      </Fab>

      <SDRCampaignModal
        open={campaignModalOpen}
        onClose={handleCloseCampaignModal}
        campaignId={selectedCampaign?.id}
        onSave={handleSaveCampaign}
      />

      <SDRLeadUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onSuccess={() => {
          fetchCampaigns();
          fetchStats();
        }}
      />

      <ConfirmationModal
        title={i18n.t("sdr.confirmationModal.deleteCampaign.title")}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteCampaign(deletingCampaign?.id)}
      >
        {i18n.t("sdr.confirmationModal.deleteCampaign.message", {
          name: deletingCampaign?.name,
        })}
      </ConfirmationModal>
    </MainContainer>
  );
};

export default SDRDashboard;